import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { tenantFieldConfig } from "../../config/tenantFieldConfig";
import {
  apiGet,
  apiPost,
  apiPut,
} from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { CustomButton } from "../../components/common/customButton";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { DynamicForm } from "../../components/common/DynamicForm";
import { AREA_TYPE_OPTIONS } from "../../constants/areaTypes";
import type { Tenant } from "../../types/deal";

const emptyTenant: Partial<Tenant> = {
  asset_id: undefined,
  tenant_name: "",
  tenant_sector: "",
  lease_start_date: undefined,
  lease_end_date: undefined,
  break_option_date: undefined,
  next_review_date: undefined,
  contracted_rent: undefined,
  rent_currency: "",
  total_area: undefined,
  area_type: "",
  estimated_rental_value: undefined,
  tf_erv: undefined,
  ios_area: undefined,
  ios_erv: undefined,
  ios_income: undefined,
};

const CreateTenant = ({
  tenant: initialTenant = emptyTenant,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const { currencies } = useReferenceLists();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tenant, setTenant] = useState<Partial<Tenant>>(initialTenant);

  useEffect(() => {
    apiGet("assets").then((response) => {
      if (response && response.success && response.data) {
        setAssets(response.data);
      }
    });
  }, []);

  const handleChange = (field: string, value: any) => {
    if (field === "asset") {
      // The asset select only yields the asset object; keep asset_id in sync so the
      // payload never carries a stale asset_id (the API applies asset_id after asset,
      // so a stale key would silently revert the selection on update).
      setTenant((prev) => ({
        ...prev,
        asset: value,
        asset_id: value?.id ?? null,
      }));
      return;
    }
    setTenant((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && typeof tenant.id !== "number") {
        console.error("Tenant update failed: missing tenant.id");
        return;
      }
      const response = isEdit
        ? await apiPut("tenant", tenant.id as number, tenant)
        : await apiPost("tenants", tenant);

      if (response && response.success) {
        navigate(isEdit ? `/tenants/${tenant.id}` : "/tenants");
      } else {
        console.error(
          "Tenant save failed:",
          response?.message || "Unknown error",
        );
      }
    } catch (error) {
      console.error("Error saving tenant:", error);
    }
  };

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
        <SimpleToolbar>
          {isEdit && (
            <CustomButton
              text="Cancel"
              handleButtonPress={() => navigate(`/tenants/${tenant.id}`)}
              solid={false}
              isButtonDisabled={false}
              data-position="left"
            />
          )}
          <CustomButton
            text={isEdit ? "Update Tenant" : "Create Tenant"}
            handleButtonPress={handleSubmit}
            isButtonDisabled={!tenant.tenant_name}
          />
          {isEdit && (
            <CustomButton
              text="Delete Tenant"
              isDeleteButton
              handleButtonPress={() => setDialogOpen(true)}
              isButtonDisabled={false}
              data-position="right"
            />
          )}
        </SimpleToolbar>

        <Box sx={{ width: "95%", margin: "0 auto", padding: 4 }}>
          <DynamicForm
            config={tenantFieldConfig}
            values={tenant}
            onChange={handleChange}
            optionsMap={{
              assets,
              currencies,
              areaTypes: [...AREA_TYPE_OPTIONS],
            }}
          />
        </Box>

        {typeof tenant.id === "number" && (
          <ConfirmDialog
            open={dialogOpen}
            endpoint="tenant"
            id={tenant.id}
            title="Delete Tenant"
            description="Are you sure you want to delete this tenant?"
            onClose={() => setDialogOpen(false)}
            onSuccess={() => navigate("/tenants")}
          />
        )}
      </Box>
    </Box>
  );
};

export default CreateTenant;
